/**
 * AsyncOperationQueue - Utility for serializing async operations
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for creating and managing async operation queues
 * that serialize operations to prevent concurrent execution.
 */

/**
 * Create an async operation queue
 * 
 * Creates a new queue initialized with a resolved promise.
 * 
 * @returns Queue object with enqueue method
 */
export function createAsyncQueue(): {
  queue: Promise<void>;
  enqueue: <T>(op: () => Promise<T>) => Promise<T>;
} {
  let queue: Promise<void> = Promise.resolve();

  const enqueue = <T>(op: () => Promise<T>): Promise<T> => {
    const run = queue.then(op, op);
    // Continue the chain regardless of op outcome
    queue = run.then(() => void 0).catch(() => void 0);
    return run;
  };

  return { queue, enqueue };
}

/**
 * Enqueue an operation in an existing queue
 * 
 * Adds an operation to the queue, ensuring it runs after all previous operations.
 * 
 * @param queueRef - Reference to the queue promise (will be mutated)
 * @param op - Operation to enqueue
 * @returns Promise that resolves when the operation completes, and updates queueRef.current
 */
export function enqueueOperation<T>(
  queueRef: { current: Promise<void> },
  op: () => Promise<T>
): Promise<T> {
  const run = queueRef.current.then(op, op);
  // Continue the chain regardless of op outcome
  queueRef.current = run.then(() => void 0).catch(() => void 0);
  return run;
}

/**
 * Create an enqueue function bound to a queue reference
 * 
 * Returns an enqueue function that manages a queue stored in the provided reference.
 * 
 * @param queueRef - Reference object to store the queue
 * @returns Enqueue function
 */
export function createEnqueueFunction(
  queueRef: { current: Promise<void> }
): <T>(op: () => Promise<T>) => Promise<T> {
  return <T>(op: () => Promise<T>): Promise<T> => {
    return enqueueOperation(queueRef, op);
  };
}

/**
 * Create a queue manager with enqueue functionality
 * 
 * Returns an object with an enqueue method that manages the queue internally.
 * 
 * @returns Queue manager object
 */
export function createQueueManager(): {
  enqueue: <T>(op: () => Promise<T>) => Promise<T>;
  clear: () => void;
} {
  let queue: Promise<void> = Promise.resolve();

  return {
    enqueue: <T>(op: () => Promise<T>): Promise<T> => {
      const run = queue.then(op, op);
      // Continue the chain regardless of op outcome
      queue = run.then(() => void 0).catch(() => void 0);
      return run;
    },
    clear: () => {
      queue = Promise.resolve();
    }
  };
}

